import { memo } from "react";
import { useTranslation } from "react-i18next";
import { Heart } from "lucide-react";
import type { ReactionLabel } from "../../hooks/use-live-reactions";
import { QrCode } from "./qr-code";
import { cn } from "../../lib/utils";

interface JoinCtaProps {
  url: string;
  reactionLabel: ReactionLabel | null;
  projector: boolean;
}

/**
 * Bottom-right "engagement corner":
 *  - a live reaction pill that pops in as likes arrive ("Ayşe +12 reacted"),
 *  - a QR card inviting guests to scan, share photos and send love.
 * This is the loop that pulls phones out of pockets.
 */
function JoinCtaImpl({ url, reactionLabel, projector }: JoinCtaProps) {
  const { t } = useTranslation();
  const qrSize = projector ? 104 : 76;

  return (
    <div className="pointer-events-none absolute bottom-[3.5vmin] right-[3.5vmin] z-20 flex flex-col items-end gap-3">
      {reactionLabel && (
        <div
          key={`${reactionLabel.name}-${reactionLabel.count}`}
          className="animate-in fade-in slide-in-from-bottom-2 duration-300 flex items-center gap-2 rounded-full bg-white/12 backdrop-blur-md border border-white/15 pl-2.5 pr-3.5 py-1.5 shadow-xl"
        >
          <span className="flex items-center justify-center size-6 rounded-full bg-rose-500/90">
            <Heart className="size-3.5 text-white" fill="currentColor" strokeWidth={0} />
          </span>
          <span className="text-white/90 text-sm font-semibold whitespace-nowrap">
            {reactionLabel.count > 1
              ? t("slideshow.reactedOther", { name: reactionLabel.name, count: reactionLabel.count - 1 })
              : t("slideshow.reactedOne", { name: reactionLabel.name })}
          </span>
        </div>
      )}

      <div
        className={cn(
          "flex items-center gap-3 rounded-2xl bg-white/12 backdrop-blur-md border border-white/15 shadow-xl",
          projector ? "p-3" : "p-2.5",
        )}
      >
        <div className="rounded-lg bg-white p-1.5 shadow-inner">
          <QrCode value={url} size={qrSize} />
        </div>
        <div className={cn("pr-1 hidden sm:block", projector ? "max-w-[180px]" : "max-w-[150px]")}>
          <p className={cn("text-white font-semibold leading-tight", projector ? "text-base" : "text-sm")}>
            {t("slideshow.scanToJoin")}
          </p>
          <p className="text-white/60 text-xs mt-1 leading-snug">{t("slideshow.scanToJoinHint")}</p>
          <p className="mt-2 flex items-center gap-1.5 text-white/80 text-xs">
            <Heart className="size-3 text-rose-400 ss-pulse" fill="currentColor" strokeWidth={0} />
            {t("slideshow.tapToReact")}
          </p>
        </div>
      </div>
    </div>
  );
}

export const JoinCta = memo(JoinCtaImpl);
